import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import StaffAvatar from './StaffAvatar';
import { supabase } from '../utils/supabaseClient';

export const STAFF_OWNER_ROLES = ['admin', 'outreach', 'clinical'];
const STAFF_OWNER_SELECT_FIELDS = 'id, full_name, email, role, avatar_url';

function normalizeText(value) {
  const normalized = String(value || '').trim();
  return normalized || '';
}

function buildOwnerOption(record = {}) {
  return {
    id: normalizeText(record.id),
    full_name: normalizeText(record.full_name),
    email: normalizeText(record.email),
    role: normalizeText(record.role),
    avatar_url: normalizeText(record.avatar_url),
  };
}

function buildCurrentUserOption({ currentUser = null, currentProfile = null } = {}) {
  const id = normalizeText(currentUser?.id);
  if (!id) return null;

  return buildOwnerOption({
    id,
    full_name: currentProfile?.full_name || currentUser?.user_metadata?.full_name || '',
    email: currentProfile?.email || currentUser?.email || '',
    role: currentProfile?.role || '',
    avatar_url: currentProfile?.avatar_url || '',
  });
}

function formatRoleLabel(role) {
  const value = normalizeText(role);
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : '';
}

function getOwnerPrimaryLabel(option) {
  return option?.full_name || option?.email || 'Staff user';
}

function getOwnerSecondaryLabel(option) {
  const parts = [normalizeText(option?.email), formatRoleLabel(option?.role)].filter(Boolean);
  return parts.join(' • ');
}

function mergeOwnerOptions(profiles, fallbackOption) {
  const unique = new Map();

  (profiles || []).forEach((profile) => {
    const option = buildOwnerOption(profile);
    if (option.id) unique.set(option.id, option);
  });

  if (fallbackOption?.id && !unique.has(fallbackOption.id)) {
    unique.set(fallbackOption.id, fallbackOption);
  }

  return Array.from(unique.values()).sort((left, right) => (
    getOwnerPrimaryLabel(left).localeCompare(getOwnerPrimaryLabel(right), undefined, { sensitivity: 'base' })
  ));
}

function OwnerOptionContent({ option }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
      <StaffAvatar
        avatarUrl={option?.avatar_url || ''}
        fullName={getOwnerPrimaryLabel(option)}
        size={28}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {getOwnerPrimaryLabel(option)}
        </Typography>
        {getOwnerSecondaryLabel(option) ? (
          <Typography variant="caption" color="text.secondary" noWrap>
            {getOwnerSecondaryLabel(option)}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

export default function StaffOwnerSelect({
  value,
  onChange,
  currentUser = null,
  currentProfile = null,
  label = 'Owner',
  helperText = '',
  disabled = false,
}) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const fallbackOption = useMemo(
    () => buildCurrentUserOption({ currentUser, currentProfile }),
    [currentProfile, currentUser]
  );

  useEffect(() => {
    if (typeof onChange !== 'function' || value || !fallbackOption?.id) return;
    onChange(fallbackOption.id);
  }, [fallbackOption?.id, onChange, value]);

  useEffect(() => {
    let active = true;

    const loadProfiles = async () => {
      setLoading(true);
      setLoadError('');

      const { data, error } = await supabase
        .from('profiles')
        .select(STAFF_OWNER_SELECT_FIELDS)
        .in('role', STAFF_OWNER_ROLES)
        .order('full_name', { ascending: true });

      if (!active) return;

      if (error) {
        setProfiles([]);
        setLoadError(error.message || 'Unable to load staff owners.');
      } else {
        setProfiles(data || []);
      }

      setLoading(false);
    };

    loadProfiles();

    return () => {
      active = false;
    };
  }, []);

  const options = useMemo(
    () => mergeOwnerOptions(profiles, fallbackOption),
    [fallbackOption, profiles]
  );

  const selectedOption = useMemo(() => (
    options.find((option) => option.id === value) ||
    fallbackOption ||
    null
  ), [fallbackOption, options, value]);

  const resolvedHelperText = loadError
    ? `${helperText ? `${helperText} ` : ''}Staff list unavailable. New records will default to you.`
    : loading
      ? `${helperText ? `${helperText} ` : ''}Loading staff owners...`
      : helperText;

  return (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel id={`${label}-owner-select-label`}>{label}</InputLabel>
      <Select
        labelId={`${label}-owner-select-label`}
        label={label}
        value={selectedOption?.id || ''}
        onChange={(event) => onChange?.(event.target.value)}
        renderValue={() => (
          selectedOption ? (
            <OwnerOptionContent option={selectedOption} />
          ) : (
            'Select owner'
          )
        )}
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            <OwnerOptionContent option={option} />
          </MenuItem>
        ))}
      </Select>
      {resolvedHelperText ? (
        <FormHelperText error={!!loadError}>{resolvedHelperText}</FormHelperText>
      ) : null}
    </FormControl>
  );
}
